import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateRubric,
  useGenerateRubric,
} from "../../api/RubricsAPI";
import { useParams } from "react-router-dom";
import CompetencySelector from "../../components/rubrics/CompetencySelector";
import RubricDisplay from "../../components/rubrics/RubricDisplay";
import { useAuth } from "../../context/AuthContext";
import { useSessionWithCompetencies } from "../../api/SessionsAPI";
import { notification } from "antd";

const RubricGenerator = () => {
  const { sessionId } = useParams();
  const { currentUser, loading } = useAuth();
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [customPrompt, setCustomPrompt] = useState("");
  const queryClient = useQueryClient();
  
  // Fetch session data
  const { 
    data: sessionData, 
    isLoading: sessionLoading, 
    refetch: refetchSession 
  } = useSessionWithCompetencies(sessionId);
  
  const [generatedRubric, setGeneratedRubric] = useState(null);
  const [isCreatingRubric, setIsCreatingRubric] = useState(false);
  
  // Add state for optimistic generations remaining
  const [optimisticGenerationsRemaining, setOptimisticGenerationsRemaining] = useState(null);
  
  // Use a ref to track the button disabled state
  const buttonDisabledRef = useRef(false);

  // Mutation for creating a rubric
  const createRubricMutation = useCreateRubric();

  // Mutation for generating a rubric
  const generateMutation = useGenerateRubric();

  // Reset states when mutations complete
  useEffect(() => {
    if (!createRubricMutation.isPending && !generateMutation.isPending) {
      setIsCreatingRubric(false);
      buttonDisabledRef.current = false;
    }
  }, [createRubricMutation.isPending, generateMutation.isPending]);

  // Update optimistic generations remaining when session data changes
  useEffect(() => {
    if (sessionData?.generationsRemaining !== undefined) {
      setOptimisticGenerationsRemaining(null); // Reset optimistic value when real data arrives
    }
  }, [sessionData?.generationsRemaining]);

  if (loading || sessionLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <div>Please log in to view reports</div>;
  }

  // Use optimistic value if available, otherwise use the real value
  const generationsRemaining = 
    optimisticGenerationsRemaining !== null 
      ? optimisticGenerationsRemaining 
      : (sessionData?.generationsRemaining ?? 0);
      
  const canGenerate = generationsRemaining > 0;

  const handleCompetencySelect = (newSelectedCompetencies) => {
    setSelectedCompetencies(newSelectedCompetencies);
  };

  const handleGenerate = () => {
    // Check if button is already disabled via the ref
    if (buttonDisabledRef.current) return;
    
    // Prevent generation if no generations remaining or already creating
    if (selectedCompetencies.length === 0 || !canGenerate || isCreatingRubric) return;

    // Immediately set the button as disabled
    buttonDisabledRef.current = true;
    
    // Set creating state to true to prevent spam clicking
    setIsCreatingRubric(true);
    
    // Optimistically update the generations remaining
    // Decrement by 1 from the current value
    setOptimisticGenerationsRemaining(Math.max(0, generationsRemaining - 1));

    generateMutation.mutate(
      {
        competencies: selectedCompetencies,
        customPrompt,
      },
      {
        onSuccess: (data) => {
          setGeneratedRubric(data);
          
          // Automatically create a rubric entry after successful generation
          createRubricMutation.mutate(
            {
              sessionId,
              rubricData: data
            },
            {
              onSuccess: () => {
                notification.success({
                  message: 'Success',
                  description: 'Rubric created successfully!',
                  placement: 'topRight'
                });
                
                // First invalidate all queries to mark them as stale
                queryClient.invalidateQueries(['session', sessionId]);
                queryClient.invalidateQueries(['session', sessionId, 'withCompetencies']);
                queryClient.invalidateQueries(['session', sessionId, { includeRubricCount: true }]);
                
                // Then force a refetch of the session data
                refetchSession();
                
                // Also try a second refetch after a delay to ensure we get the latest data
                setTimeout(() => {
                  refetchSession();
                  // Reset creating state after everything is done
                  setIsCreatingRubric(false);
                  buttonDisabledRef.current = false;
                }, 1000);
              },
              onError: (error) => {
                notification.error({
                  message: 'Error',
                  description: `Failed to save rubric: ${error.message}`,
                  placement: 'topRight'
                });
                
                // Rollback the optimistic update on error
                setOptimisticGenerationsRemaining(null);
                
                setIsCreatingRubric(false);
                buttonDisabledRef.current = false;
              }
            }
          );
        },
        onError: (error) => {
          notification.error({
            message: 'Error',
            description: `Failed to generate rubric: ${error.message}`,
            placement: 'topRight'
          });
          
          // Rollback the optimistic update on error
          setOptimisticGenerationsRemaining(null);
          
          setIsCreatingRubric(false);
          buttonDisabledRef.current = false;
        }
      }
    );
  };

  // Calculate if the button should be disabled
  const isButtonDisabled = 
    selectedCompetencies.length === 0 || 
    !canGenerate || 
    isCreatingRubric || 
    generateMutation.isPending || 
    buttonDisabledRef.current;

  return (
    <div className="py-6 px-10">
      <div className="flex flex-col mb-6">
        <h1 className="font-semibold text-2xl">Generate a Rubric</h1>
        <div className="flex flex-row justify-between">
          <p className="text-gray-600 mt-1">Session ID: {sessionId}</p>
          <p className={`font-semibold mt-2 ${canGenerate ? 'text-primary' : 'text-red-500'}`}>
            Generations remaining: {generationsRemaining}/3
          </p>
        </div>
      </div>

      <div className="flex gap-6">
        <div className="w-2/5">
          <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-3">
                Competencies Selection
              </h2>
            <CompetencySelector
              onCompetencySelect={handleCompetencySelect}
              selectedCompetencies={selectedCompetencies}
              sessionId={sessionId}
              disabled={!canGenerate || isCreatingRubric || buttonDisabledRef.current}
            />

            <div className="my-6">
              <h2 className="text-lg font-medium mb-3">
                Custom Instructions (Optional)
              </h2>
              <textarea
                className="w-full border rounded-md p-3"
                placeholder="Add any specific instructions for generating the rubric (max 150 characters)"
                maxLength={150}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={3}
                disabled={!canGenerate || isCreatingRubric || buttonDisabledRef.current}
              />
              <div className="text-right text-sm text-gray-500">
                {customPrompt.length}/150
              </div>
            </div>

            <button
              className={`w-full py-3 px-4 rounded-md text-white font-medium
                                ${
                                  !isButtonDisabled
                                    ? "bg-primary hover:bg-primaryLight"
                                    : "bg-gray-400 cursor-not-allowed"
                                }`}
              disabled={isButtonDisabled}
              onClick={handleGenerate}
            >
              {generateMutation.isPending ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Generating...
                </div>
              ) : !canGenerate ? (
                "No Generations Remaining"
              ) : isCreatingRubric || buttonDisabledRef.current ? (
                "Processing..."
              ) : (
                "Generate Rubric"
              )}
            </button>
            
            {!canGenerate && (
              <div className="mt-2 text-center text-red-500 text-sm">
                You have used all available rubric generations for this session.
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Generated Rubric Display */}
        <div className="w-3/5">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium mb-4">Generated Rubric</h2>

            {generateMutation.isPending || (isCreatingRubric && !generatedRubric) ? (
              <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="mt-4">Generating rubric...</p>
              </div>
            ) : !generatedRubric ? (
              <div className="flex flex-col items-center justify-center h-[400px] text-gray-500">
                <svg
                  className="w-16 h-16 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-center">
                  {canGenerate 
                    ? "Select competencies and generate a rubric to see it here."
                    : "You have used all available rubric generations for this session."}
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
                <RubricDisplay rubricData={generatedRubric} />
                
                {createRubricMutation.isPending && (
                  <div className="mt-4 flex items-center justify-center text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500 mr-2"></div>
                    <p>Saving rubric...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RubricGenerator;
